import React, { useEffect, useMemo, useState } from "react"

import { useSearchParams } from "react-router-dom"

import {
    AvailableCoverageHistorySortDirection,
    AvailableCoverageHistorySortField,
} from "@common-shared/config/coverageHistory.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import type { IPagination } from "@common-shared/types/general.types.ts"
import { ICoverageHistoryListSort } from "@common-shared/types/internalApis/coverageHistory.types.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../../config/policies.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook.ts"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    policiesSetAgenticSort,
    policiesSetAssignedTasksSort,
    policiesSetHistorySort,
    policiesSetMedicaidSort,
    policiesSetOverridesSort,
    policiesSetPlanYearResetsSort,
    policiesSetReportsSort,
    policiesSetTaskAssignmentsSort,
    policiesSetVisualizationsCollapseTrigger,
} from "../../../store/slices/policies.slice"
import {
    policiesGetAgenticList,
    policiesGetAssignedTasksList,
    policiesGetHistoryList,
    policiesGetMedicaidList,
    policiesGetOverridesList,
    policiesGetPlanYearResetsList,
    policiesGetReportsList,
    policiesReloadList,
} from "../../../store/thunks/policies.thunks"
import { taskAssignmentsGetList } from "../../../store/thunks/taskAssignments.thunks.ts"
import { PoliciesListPaginationComponent } from "./policiesListPagination.component"
import { PoliciesListTableComponent } from "./policiesListTable.component"

export const PoliciesListComponent: React.FunctionComponent<
    IPoliciesListComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const [searchParams, setSearchParams] = useSearchParams()

    const scrollableTableBoundaries = useScrollableElementBoundaries(
        props.scrollableElementRef,
    )

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const policiesState = useAppSelector(policiesSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const {
        policyItems,
        nextPagination,
        sortConfiguration,
        filtersConfiguration,
        isGettingListError,
    } = useMemo(() => {
        return PoliciesListHelper.getViewTypedStateData(
            props.listType,
            policiesState,
        )
    }, [props.listType, policiesState])

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                props.listType +
                    GENERAL_CONFIG.browserStorageKeys
                        .policiesResultsPerPageSuffix,
                undefined,
                BrowserStorageType.sessionStorage,
            ) || POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const [initialLoadTriggered, setInitialLoadTriggered] =
        useState<boolean>(false)
    const [reloadListTrigger, setReloadListTrigger] = useState<number>(
        new Date().getTime(),
    )

    // Save results per page selection in session storage
    useEffect(() => {
        BrowserStorageHelper.set(
            props.listType +
                GENERAL_CONFIG.browserStorageKeys.policiesResultsPerPageSuffix,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption, props.listType])

    const isListLoading = useMemo<boolean>(() => {
        switch (props.listType) {
            case PoliciesListType.REPORTS:
                return RuntimeHelper.isGetPoliciesReportsLoading()

            case PoliciesListType.OVERRIDES:
                return RuntimeHelper.isGetPoliciesOverridesLoading()

            case PoliciesListType.HISTORY_SEARCH:
                return RuntimeHelper.isGetPoliciesHistoryLoading()

            case PoliciesListType.PLAN_YEAR_RESETS:
                return RuntimeHelper.isGetPoliciesPlanYearResetsLoading()

            case PoliciesListType.MEDICAID:
                return RuntimeHelper.isGetPoliciesMedicaidLoading()

            case PoliciesListType.AGENTIC:
                return RuntimeHelper.isGetPoliciesAgenticLoading()

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return RuntimeHelper.isGetPoliciesAssignedTasksLoading()

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return RuntimeHelper.isTaskAssignmentsGetListLoading()
        }
    }, [runtimeState.isLoading, props.listType])

    // Once reload trigger happens -> reload full list with needed length / filters / etc
    // Don't reload list if there are items and through url was asked not to
    useEffect(() => {
        !initialLoadTriggered && setInitialLoadTriggered(true)

        if (
            typeof nextPagination !== "undefined" &&
            searchParams.get(
                GENERAL_CONFIG.urlSearchParamsKeys.ignoreListReload,
            )
        ) {
            setTimeout(() => {
                removeIgnoreListSearchParam()
            })

            return
        }

        const thunkPromise = getItems(
            UtilHelper.getPagination(selectedPaginationOption.value),
            () => {
                removeIgnoreListSearchParam()
            },
        )

        return () => {
            thunkPromise?.abort()
        }
    }, [reloadListTrigger])

    const removeIgnoreListSearchParam = () => {
        if (
            !searchParams.get(
                GENERAL_CONFIG.urlSearchParamsKeys.ignoreListReload,
            )
        ) {
            return
        }

        searchParams.delete(GENERAL_CONFIG.urlSearchParamsKeys.ignoreListReload)
        setSearchParams(searchParams)
    }

    // Once filters || pagination || sort length change and initial was loaded -> reload list
    useEffect(() => {
        // Prevent duplicate requests on initial component load
        // Since we are setting filters from state/url/etc
        if (!initialLoadTriggered) {
            return
        }

        setReloadListTrigger(new Date().getTime())
    }, [
        JSON.stringify(filtersConfiguration),
        selectedPaginationOption,
        sortConfiguration,
    ])

    const onReloadList = () => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            policiesReloadList({
                practice: selectedPracticeRole.practice,
                payload: {
                    policiesViewType: props.listType,
                },
            }),
        )
    }

    const getItems = (pagination?: IPagination, onSuccess?: () => void) => {
        if (!selectedPracticeRole) {
            return
        }

        const neededPagination = pagination || nextPagination

        if (neededPagination.moreAvailable === false) {
            return
        }

        // Clear selection whenever we fetch a new list slice. Pagination `start` often does not
        // change when going from page 1 → 2 (both use the same offset), so header-only clearing
        // keyed on `nextPagination.start` would miss that case and leave stale IDs selected.
        if (props.policiesSelectable) {
            props.onDeselectedAllItems?.()
        }

        const actionArgs = {
            practice: selectedPracticeRole.practice,
            payload: {
                listType: props.listType,
                pagination: neededPagination,
                filters: filtersConfiguration,
                sort: sortConfiguration,
            },
            onSuccess,
        }

        switch (props.listType) {
            case PoliciesListType.REPORTS:
                return dispatch(policiesGetReportsList(actionArgs))

            case PoliciesListType.HISTORY_SEARCH:
                return dispatch(policiesGetHistoryList(actionArgs))

            case PoliciesListType.OVERRIDES:
                return dispatch(policiesGetOverridesList(actionArgs))

            case PoliciesListType.PLAN_YEAR_RESETS:
                return dispatch(policiesGetPlanYearResetsList(actionArgs))

            case PoliciesListType.MEDICAID:
                return dispatch(policiesGetMedicaidList(actionArgs))

            case PoliciesListType.AGENTIC:
                return dispatch(policiesGetAgenticList(actionArgs))

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return dispatch(policiesGetAssignedTasksList(actionArgs))

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return dispatch(taskAssignmentsGetList(actionArgs))
        }
    }

    const onSortChanged = (newSortField: AvailableCoverageHistorySortField) => {
        const payload: ICoverageHistoryListSort = {
            sortBy: newSortField,

            sortDirection:
                newSortField !== sortConfiguration?.sortBy
                    ? AvailableCoverageHistorySortDirection.DESC
                    : sortConfiguration.sortDirection ===
                        AvailableCoverageHistorySortDirection.DESC
                      ? AvailableCoverageHistorySortDirection.ASC
                      : AvailableCoverageHistorySortDirection.DESC,
        }

        BrowserStorageHelper.set(
            props.listType +
                GENERAL_CONFIG.browserStorageKeys
                    .policiesSortConfigurationSuffix,
            payload,
        )

        switch (props.listType) {
            case PoliciesListType.REPORTS:
                dispatch(policiesSetReportsSort(payload))

                break

            case PoliciesListType.HISTORY_SEARCH:
                dispatch(policiesSetHistorySort(payload))

                break

            case PoliciesListType.OVERRIDES:
                dispatch(policiesSetOverridesSort(payload))

                break

            case PoliciesListType.PLAN_YEAR_RESETS:
                dispatch(policiesSetPlanYearResetsSort(payload))

                break

            case PoliciesListType.MEDICAID:
                dispatch(policiesSetMedicaidSort(payload))

                break

            case PoliciesListType.AGENTIC:
                dispatch(policiesSetAgenticSort(payload))

                break

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                dispatch(policiesSetAssignedTasksSort(payload))

                break

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                dispatch(policiesSetTaskAssignmentsSort(payload))

                break
        }
    }

    return (
        <div className={`relative ${props.className}`}>
            <PoliciesListTableComponent
                scrollableElementRef={props.scrollableElementRef}
                policies={policyItems}
                columns={props.columns}
                listType={props.listType}
                isListLoading={isListLoading}
                policiesSelectable={props.policiesSelectable}
                sortEnabled={props.sortEnabled}
                sortConfiguration={sortConfiguration}
                onSetSortField={onSortChanged}
                onSelectedItems={props.onSelectedItems}
                onDeselectedItems={props.onDeselectedItems}
                onDeselectedAllItems={props.onDeselectedAllItems}
                selectedItems={props.selectedItems}
                onChangeColumnOrder={props.onChangeColumnOrder}
                isSinglePolicySelectable={props.isSinglePolicySelectable}
            />

            {!policyItems.length && !isListLoading ? (
                <>
                    <div className="fixed z-10 w-screen text-center mt-[32px]">
                        {isGettingListError ? (
                            <div className="text-center">
                                <PrimaryText typography="h6" centered>
                                    Error when performing the search. Please try
                                    again.
                                </PrimaryText>

                                <ButtonElement
                                    className="mt-[24px]"
                                    htmlType="button"
                                    type="primary"
                                    label="Try again"
                                    onClick={onReloadList}
                                />
                            </div>
                        ) : (
                            <PrimaryText typography="h6" centered>
                                No policies found
                            </PrimaryText>
                        )}
                    </div>

                    {/*space holder since above is fixed to move with scroll*/}
                    <div className="mb-[96px]" />
                </>
            ) : null}

            {!isListLoading && (
                <PoliciesListPaginationComponent
                    className="md:px-[24px] px-[48px]"
                    style={{
                        right: scrollableTableBoundaries?.x,
                    }}
                    getNewItems={getItems}
                    nextPagination={nextPagination}
                    onPaginationChange={() =>
                        dispatch(policiesSetVisualizationsCollapseTrigger())
                    }
                    selectedPaginationOption={selectedPaginationOption}
                    setSelectedPaginationOption={setSelectedPaginationOption}
                />
            )}
        </div>
    )
}

interface IPoliciesListComponentProps {
    className?: string
    scrollableElementRef?: React.RefObject<HTMLDivElement>
    listType: PoliciesListType
    columns: IPolicyColumnConfiguration[]
    onChangeColumnOrder?: (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => void
    sortEnabled?: boolean
    policiesSelectable?: boolean
    selectedItems?: IOneVerifyCoverageCheckHistory[]
    onSelectedItems?: (items: IOneVerifyCoverageCheckHistory[]) => void
    onDeselectedItems?: (items: IOneVerifyCoverageCheckHistory[]) => void
    onDeselectedAllItems?: () => void
    isSinglePolicySelectable?: (item: IOneVerifyCoverageCheckHistory) => boolean
}
